import { CachedItem } from "@lib-shared/lib";
import { appSettings } from "core/config";
import { Key, LookupPlaceholders } from "core/constants";
import { logApiError } from "core/lib";
import { getContactDetailsHtml, getDashboardHtmlLink, getCustomerCareHtml, getContactTimesHtml } from "core/lib/html-helpers";
import LookupService, { ErrorCodeLookup } from "core/services/cef/lookup.service";
import ClearErrorStore from "./clear-error.store";

const placeholders = [
    { key: LookupPlaceholders.dashboard, html: getDashboardHtmlLink },
    { key: LookupPlaceholders.contactDetails, html: getContactDetailsHtml },
    { key: LookupPlaceholders.customerCare, html: getCustomerCareHtml },
    { key: LookupPlaceholders.contactTimes, html: getContactTimesHtml },
    { key: LookupPlaceholders.ussd, html: () => "" }, //USSD not implemented yet for commercial
];

const errorCodeFilter = appSettings.clearError.filter;

export type ErrorCode = {
    code: string;
    title: string;
    description: string;
    // solution: string;
}

class ErrorCodeFormatter {
    private _formatText(value: string) {
        return value.split("\n").join("<br/>");
    }

    private _replacePlaceholders(text: string) {
        text = this._formatText(text);

        placeholders.forEach((x) => {
            text = text.replace(x.key, x.html());
        });

        return text;
    };

    private _sortErrorCodes(codes: ErrorCode[]) {
        codes.sort((a: ErrorCode, b: ErrorCode) => {
            const letterA = a.code.substring(0, 1);
            const letterB = a.code.substring(0, 1);

            if (letterA !== letterB) {
                return letterA.toLowerCase().localeCompare(letterB.toLowerCase());
            }

            const codeA = parseInt(a.code.substring(1));
            const codeB = parseInt(b.code.substring(1));
            return codeA - codeB;
        });

        return codes;
    }

    ConvertAndFormat(errorCodes: ErrorCodeLookup[]) {
        const codes: ErrorCode[] = errorCodes.map((x) => {
            return {
                code: x.errorCode,
                title: this._replacePlaceholders(x.errorDescription),
                description: this._replacePlaceholders(x.causeDescription)
                // solution: this._replacePlaceholders(x.solutionDescription)
            };
        });

        return this._sortErrorCodes(codes);
    };
}

const ErrorCodeLookupProcessor = {
    process(errorCodes: ErrorCodeLookup[], codeFilter: string[]) {
        const filterErrorCodes = () => {
            errorCodes = errorCodes.filter(x => codeFilter.includes(x.errorCode));
        }

        const getUniqueErrorCodes = () => {
            const added: string[] = [];
            const unique: ErrorCodeLookup[] = [];

            errorCodes.forEach((x: ErrorCodeLookup) => {
                if (added.includes(x.errorCode)) {
                    return;
                }

                added.push(x.errorCode);
                unique.push(x);
            });

            errorCodes = unique;
        }

        const sortErrorCodes = () => {
            errorCodes.sort((a, b) => {
                const codeA = parseInt(a.errorCode.substring(1));
                const codeB = parseInt(b.errorCode.substring(1));
                return codeA - codeB;
            });
        }

        filterErrorCodes();
        getUniqueErrorCodes();
        sortErrorCodes();

        return errorCodes;
    }
}

const errorCodesCache = new CachedItem(Key.errorCodes, true, 7200);

class ClearErrorStoreManager {
    private _store: ClearErrorStore;

    constructor(store: ClearErrorStore) {
        this._store = store;
    }

    get hasData() {
        return !this._store.isLoading && (this._store.errorCodes?.length > 0 ?? false);
    }

    get store() {
        return this._store;
    }

    reset() {
        this._store.reset(false);
    }

    async fetchCodesAsync() {
        if (this._store.isLoading) {
            return;
        }

        this._store.updateIsLoading(true);
        this._store.updateHasError(false);

        if (errorCodesCache.isCached) {
            console.log("Using cached error codes");
            this._store.updateErrorCodes(errorCodesCache.value);
            this._store.updateIsLoading(false);
            return;
        }

        try {
            console.log("Fetching error codes");

            const response = await new LookupService().getErrorCodes();

            const errorCodes = response.data.lookups.errorCodes;

            this._processApiData(errorCodes);
        } catch (error) {
            logApiError(error);

            this._store.updateHasError(true);
        }

        this._store.updateIsLoading(false);
    }

    private _processApiData(data: ErrorCodeLookup[]) {
        data = ErrorCodeLookupProcessor.process(data, errorCodeFilter);

        const formatter = new ErrorCodeFormatter();
        const codes = formatter.ConvertAndFormat(data);

        this._store.updateErrorCodes(codes);
        errorCodesCache.set(codes);
    };
}

export default ClearErrorStoreManager;
